import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { MemoryHistory } from 'history';

import classNames from 'classnames';

import { Focusable } from 'components/focus';
import { useFocusManager } from 'hooks/focus';
import FocusGroup from 'components/focus/FocusGroup';
import Selectable from 'components/focus/Selectable';

import LogoPage from 'components/pages/LogoPage';
import WithLoading from 'components/common/WithLoading';

import useTracking from 'hooks/analytics';
import { getPageValues } from 'support/analytics/analyticsHelper';

import applicationActions from 'common/support/applicationActions';

import QrCode from 'components/common/QrCode';
import { VideoCtvRedux } from 'store-types';
import Button from './PrivacyButton';
import styles from './PrivacyPageGDPR.module.scss';

interface CmpUserInterfaceData {
  multiParagraphDescription: string[];
  primaryButtonText: string;
  secondaryButtonText: string;
  subMenuMultiParagraphDescription: string[];
  tertiaryButtonText: string;
  qrCodeUrl: string;
}

interface CmpGDPR {
  addEventListener: (event: string, callback: () => void) => void;
  consent: {
    sync: () => void;
    acceptAll: () => void;
    rejectAll: () => void;
  };
  userInterface: {
    getState: () => CmpUserInterfaceData;
  };
}

declare const window: Window & {
  cmp: CmpGDPR;
  ParamountCmp: {
    PrivacyEvent: {
      USER_INTERFACE_READY: string;
      CONSENT_UPDATED: string;
    };
  };
};

interface TrackPageData {
  pageType: string;
  [key: string]: unknown;
}

const trackPrivacyPageGDPR = (tracker: (data: TrackPageData) => void) => {
  const pageData: TrackPageData = {
    pageType: 'privacy',
    ...getPageValues('privacy'),
  };

  tracker(pageData);
};

const PrivacyPageGDPR = () => {
  const { cmp } = window;
  const history = useHistory() as MemoryHistory;
  const focusManager = useFocusManager();
  const ksLoading = useSelector(
    (state: VideoCtvRedux) => state.kitchenSink.loading,
  );
  const isActive = true;
  const [qrConfirmationState, setQrConfirmationState] = useState(false);
  const [confirmButtonText, setConfirmButtonText] = useState<string|undefined>('Go Back');

  const scrollableDivRef = useRef<(HTMLDivElement & { hasFocus?: boolean })|null>(null);

  useEffect(() => {
    focusManager.setCurrentFocus('privacy-scrollBox');
  }, [focusManager]);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;
    if (isActive) {
      intervalId = setInterval(() => {
        window.cmp.consent.sync();
      }, 5000);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [isActive]);

  // let userConsent = false;

  // cmp.addEventListener(window.ParamountCmp.PrivacyEvent.CONSENT_READY, () => {
  //   const consent = cmp.consent.getState();
  //   const consentArray = Object.values(consent);

  //   for (const status of consentArray) {
  //     if (!status) {
  //       userConsent = false;
  //       break;
  //     } else {
  //       userConsent = true;
  //     }
  //   }
  // });

  let cmpUserInterfaceData: CmpUserInterfaceData | undefined;

  cmp.addEventListener(
    window.ParamountCmp.PrivacyEvent.USER_INTERFACE_READY,
    () => {
      // get ParamountCmp UI data
      cmpUserInterfaceData = cmp.userInterface.getState();
    },
  );

  cmp.addEventListener(window.ParamountCmp.PrivacyEvent.CONSENT_UPDATED, () => {
    // show confirmation button
    if (!qrConfirmationState) {
      setQrConfirmationState(true);
    }
  });
  /*
  cmp.addEventListener(window.ParamountCmp.PrivacyEvent.CONSENT_SYNCED, () => {
    // setTimeout(history.goBack, 5000)
  });
  */

  const paragraphText = cmpUserInterfaceData?.multiParagraphDescription;
  const acceptButtonText = cmpUserInterfaceData?.primaryButtonText;
  const rejectButtonText = cmpUserInterfaceData?.secondaryButtonText;
  const submenuText = cmpUserInterfaceData?.subMenuMultiParagraphDescription;
  const { tertiaryButtonText } = cmpUserInterfaceData ?? {};

  useEffect(() => {
    setConfirmButtonText(qrConfirmationState ? tertiaryButtonText : 'Go Back');
  }, [qrConfirmationState, tertiaryButtonText]);
  const { qrCodeUrl } = cmpUserInterfaceData ?? {};
  const scrollDown = (action: string) => {
    let scrollBy = 0;
    if (action === 'moveDown') {
      scrollBy = 100;
    } else if (action === 'moveUp') {
      scrollBy = -100;
    } else {
      return;
    }
    scrollableDivRef.current?.scrollBy({ top: scrollBy });
  };

  const onAction = (action: string) => {
    if (focusManager.currentFocusId === 'privacy-scrollBoxText') {
      if (action === applicationActions.back) {
        focusManager.setCurrentFocus('privacy-scrollBox');
      }
      return true;
    }

    if (action === applicationActions.back) {
      focusManager.setCurrentFocus('privacy-back-button');
      return true;
    }

    return false;
  };

  const onActionBack = (action: string) => {
    if (action === applicationActions.back) {
      focusManager.setCurrentFocus('privacy-back-button');
      return true;
    }

    if (
      action === applicationActions.moveLeft &&
      focusManager.currentFocusId === 'privacy-back-button'
    ) {
      focusManager.setCurrentFocus('privacy-scrollBox');
      return true;
    }

    return false;
  };

  const onClickText = () => {
    focusManager.setCurrentFocus('privacy-scrollBoxText');
  };

  const onClickAccept = () => {
    cmp.consent.acceptAll();
  };

  const onClickReject = () => {
    cmp.consent.rejectAll();
  };

  const onClickBack = () => {
    history.goBack();
  };

  const { trackPage: tracker } = useTracking();
  useEffect(() => {
    trackPrivacyPageGDPR(tracker);
  }, [tracker]);

  return (
    <Focusable
      focusId="privacy-page"
      className={classNames('w-100 h-100 fixed-top', styles.page)}
      data-testid="privacy-page"
      render={() => (
        <WithLoading loading={ksLoading}>
          <LogoPage
            className={classNames(
              'd-flex flex-row align-items-top position-relative',
              styles.logoPage,
            )}
          >
            <div
              className={classNames(
                'd-flex',
                'flex-column',
                'align-items-stretch',
                'justify-content-between',
                'flex-grow-1',
              )}
            >
              <FocusGroup
                className={classNames(
                  'd-flex',
                  'flex-row',
                  'align-items-center',
                  'justify-content-between',
                  styles.contentContainer,
                )}
                onActionOverride={onAction}
                horizontal
              >
                <Selectable
                  focusId="privacy-scrollBox"
                  className={styles.scrollBoxContainer}
                  ttsText="Privacy Policy"
                  onSelect={onClickText}
                  render={({ hasFocus }: { hasFocus: boolean }) => (
                    <div
                      className={classNames(
                        styles.boxBorder,
                        hasFocus ? styles.focused : styles.unfocusedNoBorder,
                      )}
                    >
                      <Focusable
                        focusId="privacy-scrollBoxText"
                        className={classNames(
                          styles.scrollBoxText,
                          scrollableDivRef.current?.hasFocus
                            ? styles.focusedScrollBar
                            : '',
                        )}
                        data-testid="privacy-scrollBoxText"
                        ttsText={paragraphText}
                        render={() => (paragraphText||[]).join('\r\n\n')}
                        onAction={scrollDown}
                        ref={scrollableDivRef}
                        onFocusSet={(value: boolean) => {
                          if (value) {
                            scrollableDivRef.current?.classList.add(
                              styles.focusedScrollBar,
                            );
                          } else {
                            scrollableDivRef.current?.classList.remove(
                              styles.focusedScrollBar,
                            );
                          }
                        }}
                      />
                    </div>
                  )}
                />
                <FocusGroup
                  className={classNames(
                    'd-flex flex-column ml-5 w-100 h-100 justify-content-around',
                    styles.backButtonContainer,
                  )}
                  onActionOverride={onActionBack}
                  vertical
                  focusId="buttons"
                >
                  <Button
                    type="button"
                    className={classNames('w-100')}
                    ttsText={acceptButtonText as string}
                    onClick={onClickAccept}
                  >
                    {acceptButtonText}
                  </Button>
                  <Button
                    type="button"
                    className={classNames('w-100')}
                    ttsText={rejectButtonText as string}
                    onClick={onClickReject}
                  >
                    {rejectButtonText}
                  </Button>

                  <QrCode url={qrCodeUrl} />
                  <Button
                    type="button"
                    focusId="privacy-back-button"
                    className={classNames('w-100')}
                    ttsText={confirmButtonText as string}
                    onClick={onClickBack}
                  >
                    {' '}
                    {confirmButtonText}{' '}
                  </Button>
                </FocusGroup>
              </FocusGroup>
              <div
                className={classNames(
                  'd-flex flex-column align-items-start align-self-stretch w-100',
                  styles.subMenuContainer,
                )}
              >
                {(submenuText||[]).map(paragraph => {
                  return <p> {paragraph.toString()}</p>;
                })}
              </div>
            </div>
          </LogoPage>
        </WithLoading>
      )}
    />
  );
};

export default PrivacyPageGDPR;
